import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useProjectsProvider,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import { useIntl } from 'react-intl';
import { requestQuoteAnalyticsEvents } from './analytics';
import { MiniSelectionView } from './MiniSelectionView';
import {
  extractLastSegment,
  openCreateProjectModal,
} from './requestQuote.utils';
import {
  AssignedRepSelect,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  Input,
  Text,
  cn,
  SelectValue,
  InfoIcon,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '../../../../../../UILibrary';
import { definedTranslation } from '../../i18n';

type RequestQuoteEditableContentProps = {
  purchasingQuantity: string;
  selectedVariant: string;
  selectedVariantImg: string | null;
  unitOfMeasureMap: Record<string, string>;
  setPurchasingQuantity: (value: string) => void;
  setSelectedVariant: (variant: string) => void;
  getAnalyticsEventOptions: () => Record<string, any>;
  setReps: (reps: any[]) => void;
  setAssignedRepId: (assignedRepId: string) => void;
  setSelectedVariantImg: (img: string | null) => void;
};

export function RequestQuoteEditableContent({
  purchasingQuantity,
  selectedVariant,
  selectedVariantImg,
  unitOfMeasureMap,
  setPurchasingQuantity,
  getAnalyticsEventOptions,
  setReps,
  setSelectedVariantImg,
  setSelectedVariant,
  setAssignedRepId,
}: RequestQuoteEditableContentProps) {
  const intl = useIntl();
  const { projects, activeProjects } = useProjectsProvider();
  const { currentProject, setCurrentProjectId } = useCurrentProject();
  const { isProjectDataComplete } = useValidateProjectData();

  function updateProjectHandler(value: string, label: string) {
    const project = projects[value];

    // If the project is not found, open create project modal and exit
    if (!project) {
      openCreateProjectModal();

      return;
    }

    const update = () => {
      setCurrentProjectId(value);

      requestQuoteAnalyticsEvents.projectUpdated({
        ...getAnalyticsEventOptions(),
        projectId: value,
        projectName: label ?? '',
      });
    };

    // If project data is incomplete, open edit dialog and update after completion
    if (!isProjectDataComplete(project)) {
      projectsEditDialogConfig.openProjectEditDialog({
        projectId: project.externalId,
        successCallback: update,
      });

      return;
    }

    // Otherwise, update immediately
    update();
  }

  function selectVariantHandler(
    id: string,
    item?: { url?: string; color_name?: string },
  ) {
    setSelectedVariant(id);
    setSelectedVariantImg(item?.url ?? '');
  }

  const productId = window.productAttributeJson?.product_id?.value ?? '';
  const initialImage =
    window.productConfigurableJson?.images?.[productId]?.[0]?.url;
  const firstGalleryImage = window.galleryImages?.[0]?.url;
  const pluralUnitOfMeasure =
    unitOfMeasureMap[
      window?.productAttributeJson?.pricing_unit_of_measure?.value ?? ''
    ];

  const mappedProjects = Object.values(activeProjects).map((val) => {
    return {
      label: val.title,
      value: val.externalId.toString(),
    };
  });

  return (
    <>
      <div className="flex w-full pb-3 border-b border-b-[#F2F2F2]">
        <AssignedRepSelect
          onAssignedRepChange={(assignedRepId) =>
            setAssignedRepId(assignedRepId)
          }
          onRepsLoaded={(reps) => setReps(reps)}
        />
      </div>

      {window.productAttributeJson?.price_range_verbose?.value && (
        <Text variant="H2">
          {window.productAttributeJson.price_range_verbose.value}
        </Text>
      )}

      <div className="flex flex-col gap-3 py-6 border-t border-b border-[#F2F2F2]">
        <div className="flex items-center gap-2">
          <Text>
            {intl?.formatMessage(
              definedTranslation.requestQuote.confirmProjectDetailsLabel,
            )}
          </Text>
          {window.productAttributeJson?.brand_price_statement?.value && (
            <Tooltip>
              <TooltipTrigger>
                <InfoIcon />
              </TooltipTrigger>
              <TooltipContent>
                {window.productAttributeJson.brand_price_statement.value}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <Select
          value={currentProject?.externalId?.toString() ?? ''}
          direction="bottom-start"
          options={mappedProjects}
          onChange={updateProjectHandler}
        >
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={intl?.formatMessage(
                definedTranslation.requestQuote.selectProjectLabel,
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {mappedProjects.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative">
          <Input
            min={0}
            type="number"
            placeholder={intl?.formatMessage(
              definedTranslation.requestQuote.intendedPurchasingQuantityLabel,
            )}
            value={purchasingQuantity}
            onChange={(e) => {
              setPurchasingQuantity(e.target.value);
            }}
            onBlur={() => {
              requestQuoteAnalyticsEvents.purchaseQuantityUpdated({
                ...getAnalyticsEventOptions(),
                quantity: purchasingQuantity,
                unitType: pluralUnitOfMeasure,
              });
            }}
          />

          <div className="flex bg-[#F5F5F6] rounded-full h-full items-center pr-5 absolute top-0 right-0">
            {pluralUnitOfMeasure}
          </div>
        </div>
      </div>

      <MiniSelectionView
        category={
          window.productAttributeJson?.taxonomy_us?.value
            ? extractLastSegment(
                window.productAttributeJson?.taxonomy_us?.value,
              )
            : undefined
        }
        name={window.productAttributeJson?.name?.value}
        manufacturer={window.productAttributeJson?.manufacturer?.value}
        imgSrc={
          window.productConfigurableJson === undefined
            ? firstGalleryImage
            : selectedVariantImg ?? initialImage ?? firstGalleryImage
        }
      />

      {Object.keys(window.productConfigurableJson?.images ?? {}).length > 0 && (
        <div className="flex flex-col gap-3">
          <Text variant="H3">
            {intl?.formatMessage(
              definedTranslation.requestQuote.chooseVariantLabel,
            )}
          </Text>
          <div className="grid grid-cols-3 gap-3">
            {Object.keys(window.productConfigurableJson?.images ?? {}).map(
              (id) => {
                const item = window.productConfigurableJson?.images?.[id]?.[0];
                const isSelected = id === selectedVariant;

                return (
                  <div
                    className="cursor-pointer w-fit relative flex flex-col gap-2 rounded-lg"
                    onClick={() => {
                      selectVariantHandler(id, item);
                    }}
                    key={id}
                  >
                    <div
                      className={cn(
                        'brightness-95 max-w-full p-[2px] rounded-xl border-2 border-transparent',
                        {
                          'border-[#4D4E58]': isSelected,
                        },
                      )}
                    >
                      <img
                        src={`${item?.url}?width=140&height=140`}
                        className="rounded-lg"
                        alt="Product"
                      />
                    </div>
                    <Text variant="Caption1" className="text-[#4D4E58]">
                      {item?.color_name}
                    </Text>
                  </div>
                );
              },
            )}
          </div>
        </div>
      )}
    </>
  );
}
